import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Button, Card, DataTable, SectionCard, type ColumnDef } from "@/deps/ui-library";
import { useOpenItems, usePayableAnalysis, useReceivableAnalysis } from "@/api/queries";
import type {
  OpenItem,
  PayableAnalysis,
  PayableAnalysisSupplier,
  ReceivableAnalysis,
  ReceivableAnalysisCustomer,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { PageHeader } from "@/components/PageHeader";
import { bandLabels } from "@/features/reports/AgeingPage";
import { addDays, formatDate, today } from "@/lib/dates";
import { cn } from "@/lib/cn";
import { formatAmount } from "@/lib/money";
import { termsLabel } from "@/lib/paymentTerms";
import { ageText, limitUsed, paisa } from "./standing";

type Kind = "customer" | "supplier";

/** A tile or a band that can be pressed to open its table. */
type Pick =
  | { kind: "documents"; title: string; from?: string; to?: string }
  | { kind: "custom"; title: string };

interface Tile {
  key: string;
  label: string;
  value: string;
  detail: string;
  tone?: "bad";
  pick: Pick;
}

const noun = (count: number, one: string) => `${count} ${one}${count === 1 ? "" : "s"}`;

/** The due dates a band covers. It mirrors how the ledger bands amounts, from upper bounds in days. */
export function bandRange(
  asAt: string,
  days: number[],
  band: number,
): { from?: string; to?: string } {
  if (band === 0) return { from: asAt };
  const after = (days[band - 1] ?? 0) + 1;
  const upTo = days[band];
  return upTo === undefined
    ? { to: addDays(asAt, -after) }
    : { from: addDays(asAt, -upTo), to: addDays(asAt, -after) };
}

const sumOverdue = (totals: string[]) =>
  totals.slice(1).reduce((sum, value) => sum + paisa(value), 0);

/**
 * What everyone owes or is owed, as figures you can open. Pressing a tile or an ageing band opens the
 * table behind it, and pressing it again closes it.
 */
export function AnalysisPage({ kind }: { kind: Kind }) {
  return kind === "customer" ? <Receivable /> : <Payable />;
}

function Receivable() {
  const asAt = today();
  const { data, error } = useReceivableAnalysis(asAt);
  const tiles = data ? receivableTiles(data) : [];
  return (
    <Frame
      kind="customer"
      title="Receivable analysis"
      asAt={asAt}
      error={error}
      tiles={tiles}
      bucketDays={data?.bucketDays}
      bucketTotals={data?.bucketTotals}
      custom={(key) =>
        key === "behaviour" ? (
          <BehaviourTable customers={data?.customers ?? []} />
        ) : (
          <CreditTable customers={data?.customers ?? []} />
        )
      }
    />
  );
}

function Payable() {
  const asAt = today();
  const { data, error } = usePayableAnalysis(asAt);
  const tiles = data ? payableTiles(data, asAt) : [];
  return (
    <Frame
      kind="supplier"
      title="Payable analysis"
      asAt={asAt}
      error={error}
      tiles={tiles}
      bucketDays={data?.bucketDays}
      bucketTotals={data?.bucketTotals}
      custom={() => <WithholdingTable suppliers={data?.suppliers ?? []} />}
    />
  );
}

function receivableTiles(data: ReceivableAnalysis): Tile[] {
  const owing = data.customers.filter((c) => paisa(c.outstanding) > 0).length;
  const overdue = sumOverdue(data.bucketTotals);
  const limited = data.customers.filter((c) => c.creditLimit !== null);
  const over = limited.filter((c) => paisa(c.outstanding) > paisa(c.creditLimit!)).length;
  const near = limited.filter(
    (c) =>
      paisa(c.outstanding) <= paisa(c.creditLimit!) &&
      limitUsed(c.outstanding, c.creditLimit!) >= 80,
  ).length;

  return [
    {
      key: "open",
      label: "Receivable outstanding",
      value: formatAmount(data.total),
      detail: `Across ${noun(owing, "customer")}`,
      pick: { kind: "documents", title: "Open invoices" },
    },
    {
      key: "overdue",
      label: "Overdue",
      value: formatAmount(overdue / 100),
      detail: `${noun(data.overdueInvoices, "invoice")} past due`,
      tone: overdue > 0 ? "bad" : undefined,
      pick: { kind: "documents", title: "Overdue invoices", to: addDays(data.asAt, -1) },
    },
    {
      key: "behaviour",
      label: "Average days to pay",
      value: data.averageDaysToPay === null ? "None yet" : String(data.averageDaysToPay),
      detail: "Last 12 months, weighted by value",
      pick: { kind: "custom", title: "Payment behaviour by customer" },
    },
    {
      key: "credit",
      label: "Credit limit",
      value: `${over} over`,
      detail: `${near} at or above 80% of limit`,
      tone: over > 0 ? "bad" : undefined,
      pick: { kind: "custom", title: "Credit limit usage" },
    },
  ];
}

function payableTiles(data: PayableAnalysis, asAt: string): Tile[] {
  const overdue = sumOverdue(data.bucketTotals);
  return [
    {
      key: "open",
      label: "Payable outstanding",
      value: formatAmount(data.total),
      detail: `Across ${noun(data.suppliersWithOpenBills, "supplier")}`,
      pick: { kind: "documents", title: "Open bills" },
    },
    {
      key: "overdue",
      label: "Overdue",
      value: formatAmount(overdue / 100),
      detail: `${noun(data.overdueBills, "bill")} past due`,
      tone: overdue > 0 ? "bad" : undefined,
      pick: { kind: "documents", title: "Overdue bills", to: addDays(asAt, -1) },
    },
    {
      key: "week",
      label: "Due within 7 days",
      value: formatAmount(data.dueWithinWeek),
      detail: noun(data.billsDueWithinWeek, "bill"),
      pick: {
        kind: "documents",
        title: "Bills due within 7 days",
        from: asAt,
        to: addDays(asAt, 7),
      },
    },
    {
      key: "withholding",
      label: "Withholding to deduct",
      value: formatAmount(data.toDeduct),
      detail: `${formatAmount(data.withheldThisYear)} already withheld this year`,
      pick: { kind: "custom", title: "Withholding by supplier" },
    },
  ];
}

function Frame({
  kind,
  title,
  asAt,
  error,
  tiles,
  bucketDays,
  bucketTotals,
  custom,
}: {
  kind: Kind;
  title: string;
  asAt: string;
  error: unknown;
  tiles: Tile[];
  bucketDays: number[] | undefined;
  bucketTotals: string[] | undefined;
  custom: (key: string) => React.ReactNode;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const toggle = (key: string) => setSelected((current) => (current === key ? null : key));
  const documents = kind === "customer" ? "Invoices" : "Bills";
  const labels = bucketDays ? bandLabels(bucketDays) : [];
  const bands: Tile[] = labels.map((label, index) => ({
    key: `band-${index}`,
    label,
    value: formatAmount(bucketTotals?.[index] ?? "0"),
    detail: "",
    tone: index > 0 && paisa(bucketTotals?.[index] ?? "0") > 0 ? "bad" : undefined,
    pick: {
      kind: "documents",
      title: `${documents} · ${label}`,
      ...bandRange(asAt, bucketDays ?? [], index),
    },
  }));
  const chosen = [...tiles, ...bands].find((tile) => tile.key === selected);

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs
        items={[
          kind === "customer"
            ? { label: "Customers", to: "/sales/customers" }
            : { label: "Suppliers", to: "/purchases/suppliers" },
          { label: title },
        ]}
      />
      <PageHeader title={title} subtitle={`As at ${formatDate(asAt)}`} />

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((tile) => (
          <TileButton key={tile.key} tile={tile} pressed={selected === tile.key} onPress={toggle} />
        ))}
      </div>

      {bands.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-fg text-sm font-semibold">
            {kind === "customer" ? "Receivables ageing" : "Payables ageing"}
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {bands.map((band) => (
              <TileButton
                key={band.key}
                tile={band}
                pressed={selected === band.key}
                onPress={toggle}
              />
            ))}
          </div>
        </div>
      ) : null}

      {chosen ? (
        chosen.pick.kind === "documents" ? (
          <DocumentsTable
            kind={kind}
            asAt={asAt}
            title={chosen.pick.title}
            from={chosen.pick.from}
            to={chosen.pick.to}
            onClose={() => setSelected(null)}
          />
        ) : (
          <SectionCard
            title={chosen.pick.title}
            actions={<Close onClose={() => setSelected(null)} />}
            flush
          >
            {custom(chosen.key)}
          </SectionCard>
        )
      ) : null}

      <p className="text-fg-muted text-xs">
        The figures come from the ledger, so a credit or a manual journal can move a band. The
        tables list documents that are still open.
      </p>
    </div>
  );
}

function TileButton({
  tile,
  pressed,
  onPress,
}: {
  tile: Tile;
  pressed: boolean;
  onPress: (key: string) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className="cursor-pointer text-left"
      onClick={() => onPress(tile.key)}
    >
      <Card size="sm" className={cn("h-full", pressed && "ring-primary ring-2")}>
        <span className="text-fg-muted block text-xs font-medium">{tile.label}</span>
        <span
          className={cn(
            "num mt-1 block text-lg font-semibold",
            tile.tone === "bad" ? "text-destructive" : "text-fg",
          )}
        >
          {tile.value}
        </span>
        {tile.detail ? (
          <span className="text-fg-muted mt-1 block text-xs">{tile.detail}</span>
        ) : null}
      </Card>
    </button>
  );
}

function Close({ onClose }: { onClose: () => void }) {
  return (
    <Button colorScheme="default" variant="ghost" size="sm" onClick={onClose}>
      Close
    </Button>
  );
}

function PartyLink({ kind, id, name }: { kind: Kind; id: string; name: string | null }) {
  const label = name ?? "A party no longer on file";
  return kind === "customer" ? (
    <Link to="/sales/customers/$id" params={{ id }} className="text-primary hover:underline">
      {label}
    </Link>
  ) : (
    <Link to="/purchases/suppliers/$id" params={{ id }} className="text-primary hover:underline">
      {label}
    </Link>
  );
}

function DocumentsTable({
  kind,
  asAt,
  title,
  from,
  to,
  onClose,
}: {
  kind: Kind;
  asAt: string;
  title: string;
  from: string | undefined;
  to: string | undefined;
  onClose: () => void;
}) {
  const { data, isPending, error } = useOpenItems(kind, from, to);
  const columns: Array<ColumnDef<OpenItem>> = [
    {
      id: "party",
      header: kind === "customer" ? "Customer" : "Supplier",
      cell: ({ row: { original: item } }) => (
        <PartyLink kind={kind} id={item.partyId} name={item.partyName} />
      ),
    },
    {
      id: "document",
      header: kind === "customer" ? "Invoice" : "Bill",
      cell: ({ row: { original: item } }) =>
        kind === "customer" ? (
          <Link
            to="/sales/invoices/$id"
            params={{ id: item.id }}
            className="text-primary num hover:underline"
          >
            {item.number}
          </Link>
        ) : (
          <Link
            to="/purchases/bills/$id"
            params={{ id: item.id }}
            className="text-primary num hover:underline"
          >
            {item.number}
          </Link>
        ),
    },
    {
      id: "due",
      header: "Due",
      cell: ({ row: { original: item } }) => formatDate(item.due),
    },
    {
      id: "age",
      header: "Age",
      cell: ({ row: { original: item } }) => ageText(asAt, item.date, item.due),
    },
    {
      id: "open",
      header: "Open",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: item } }) => formatAmount(item.open),
    },
  ];

  return (
    <SectionCard title={title} actions={<Close onClose={onClose} />} flush>
      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
      ) : null}
      <DataTable
        aria-label={title}
        size="sm"
        enableSorting={false}
        columns={columns}
        data={data?.items ?? []}
        getRowId={(item) => item.id}
        loading={isPending}
        emptyContent="Nothing open here."
        pagination={{ mode: "manual", show: false }}
      />
      {data && data.items.length < data.total ? (
        <p className="text-fg-muted px-3 py-2 text-xs">
          Showing the oldest {data.items.length} of {data.total}.
        </p>
      ) : null}
    </SectionCard>
  );
}

function BehaviourTable({ customers }: { customers: ReceivableAnalysisCustomer[] }) {
  const rows = customers
    .filter((c) => paisa(c.outstanding) > 0)
    .sort((a, b) => (b.daysPastDue ?? -1) - (a.daysPastDue ?? -1));
  const columns: Array<ColumnDef<ReceivableAnalysisCustomer>> = [
    {
      id: "customer",
      header: "Customer",
      cell: ({ row: { original: c } }) => <PartyLink kind="customer" id={c.id} name={c.name} />,
    },
    {
      id: "terms",
      header: "Terms",
      cell: ({ row: { original: c } }) => termsLabel(c.paymentTermsDays),
    },
    {
      id: "oldest",
      header: "Oldest open invoice",
      cell: ({ row: { original: c } }) =>
        c.daysPastDue === null ? "Not yet due" : `${c.daysPastDue}d overdue`,
    },
    {
      id: "outstanding",
      header: "Outstanding",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: c } }) => formatAmount(c.outstanding),
    },
  ];
  return (
    <DataTable
      aria-label="Payment behaviour by customer"
      size="sm"
      enableSorting={false}
      columns={columns}
      data={rows}
      getRowId={(c) => c.id}
      emptyContent="No customer owes anything."
      pagination={{ mode: "manual", show: false }}
    />
  );
}

function CreditTable({ customers }: { customers: ReceivableAnalysisCustomer[] }) {
  const used = (c: ReceivableAnalysisCustomer) => limitUsed(c.outstanding, c.creditLimit ?? "0");
  const rows = customers.filter((c) => c.creditLimit !== null).sort((a, b) => used(b) - used(a));
  const columns: Array<ColumnDef<ReceivableAnalysisCustomer>> = [
    {
      id: "customer",
      header: "Customer",
      cell: ({ row: { original: c } }) => <PartyLink kind="customer" id={c.id} name={c.name} />,
    },
    {
      id: "limit",
      header: "Limit",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: c } }) => formatAmount(c.creditLimit ?? "0"),
    },
    {
      id: "outstanding",
      header: "Outstanding",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: c } }) => formatAmount(c.outstanding),
    },
    {
      id: "used",
      header: "Used",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: c } }) => `${used(c)}%`,
    },
  ];
  return (
    <DataTable
      aria-label="Credit limit usage"
      size="sm"
      enableSorting={false}
      columns={columns}
      data={rows}
      getRowId={(c) => c.id}
      emptyContent="No customer has a credit limit."
      pagination={{ mode: "manual", show: false }}
    />
  );
}

function WithholdingTable({ suppliers }: { suppliers: PayableAnalysisSupplier[] }) {
  const rows = [...suppliers].sort((a, b) => paisa(b.toDeduct) - paisa(a.toDeduct));
  const columns: Array<ColumnDef<PayableAnalysisSupplier>> = [
    {
      id: "supplier",
      header: "Supplier",
      cell: ({ row: { original: s } }) => <PartyLink kind="supplier" id={s.id} name={s.name} />,
    },
    {
      id: "category",
      header: "Category",
      cell: ({ row: { original: s } }) => s.category,
    },
    {
      id: "rate",
      header: "Rate",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: s } }) => `${s.ratePct}%`,
    },
    {
      id: "open",
      header: "Open bills",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: s } }) => formatAmount(s.openBills),
    },
    {
      id: "deduct",
      header: "To deduct",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: s } }) => formatAmount(s.toDeduct),
    },
  ];
  return (
    <DataTable
      aria-label="Withholding by supplier"
      size="sm"
      enableSorting={false}
      columns={columns}
      data={rows}
      getRowId={(s) => s.id}
      emptyContent="No supplier with an open bill has a withholding category."
      pagination={{ mode: "manual", show: false }}
    />
  );
}
